import { useQuery, useMutation } from '@apollo/client';
import {
  B1,
  DisabledTag,
  EventEmitter,
  getTranslatedText,
  Loader,
  T2,
  toast,
  BackgroundIcon,
  getParsedCrudData,
  NoDataCard,
  DateTimeFormatter
} from '@nile/components';
import EmptyContainer from '@nile/components/src/Containers/EmptyContainer';
import { GET_ALL_WORKLOGS, UPDATE_WORKLOG } from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import {
  Avatar,
  TextField,
  ButtonGroup,
  Button
} from '@superops/ui-design-system';
import {
  WorklogItemEdit,
  WorklogItemView
} from '@nile/mangifera/src/Pebbles/Worklog';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import { ContentWrapper } from 'sopslibrary';
import { cloneDeep, omit, pick } from 'lodash';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';

const Wrapper = styled(ContentWrapper)`
  background-image: url(${envConfig.imageRoot}svg/chatBg.svg);
  background-size: 360px;
  background-color: #f9f9ff;
  overflow: auto;
  align-content: start;
  .Polaris-TextField {
    margin-bottom: 0px !important;
  }
`;

const CardSection = styled.div.attrs({
  className: 'full-width'
})`
  border-radius: 4px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  background: #fff;
  padding: 20px;
  .Polaris-Avatar {
    background: linear-gradient(180deg, #88f0ff 0%, #75a6ff 97.04%);
    text-transform: uppercase;
    font-size: 16px;
    font-weight: 600;
  }
`;

const AvatarHolder = styled.div`
  display: flex;
  align-items: center;
  .react-datepicker-wrapper {
    width: 100px;
  }
  .react-datepicker__input-container > input {
    width: 100%;
  }
`;

const Details = styled.div`
  padding-left: 40px;
`;

const LoadWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EditAction = styled.div`
  .Polaris-Button {
    padding: 0 10px !important;
  }
`;

function WorklogDetails(props) {
  const { intl, ticket, id, inputVariable, isEditable } = props;
  const [worklogs, setWorklogs] = useState(props.worklogs || []);
  const inputVariables = {
    listInfo: {}
  };
  inputVariables[inputVariable] = id;
  const { loading, data, error, refetch } = useQuery(GET_ALL_WORKLOGS, {
    variables: inputVariables,
    fetchPolicy: 'network-only',
    onCompleted(res) {
      setWorklogs(res?.getWorklogs || []);
    }
  });
  const updateWorklogs = () => {
    refetch().then((res) => {
      if (res?.data?.getWorklogs) {
        setWorklogs(res?.data?.getWorklogs);
      }
    });
  };
  useEffect(() => {
    EventEmitter.subscribe('worklogChange', updateWorklogs);
    return function cleanup() {
      EventEmitter.unsubscribe('worklogChange');
    };
  }, []);
  if (loading)
    return (
      <LoadWrapper>
        <Loader />
      </LoadWrapper>
    );
  return (
    <Wrapper className="full-height">
      {worklogs.length ? (
        worklogs.map((el) => {
          return (
            <WorklogCard isEditable={isEditable} intl={intl} worklog={el} />
          );
        })
      ) : (
        <div className="full-width full-height flex center jcenter">
          <NoDataCard
            imageURL={`url(${envConfig.imageRoot}svg/worklog-empty-state.svg)`}
            text={<FormattedMessage id="ticket.details.worklog.emptyState" />}
            description={
              <FormattedMessage id="ticket.details.worklog.emptyState.description" />
            }
            type="secondary"
          />
        </div>
      )}
    </Wrapper>
  );
}

function WorklogCard(props) {
  const { intl, worklog, isEditable } = props;
  const [mode, setMode] = useState('view');
  const [worklogDate, setWorklogDate] = useState(worklog.worklogDate);
  const [notes, setNotes] = useState(worklog.notes);
  const [selectedWorklogItems, setSelectedWorklogItems] = useState(
    worklog.worklogItems
  );
  const [updateWorklog] = useMutation(UPDATE_WORKLOG);
  const [loading, setWorklogLoading] = useState(false);
  const resetWorklog = () => {
    setWorklogDate(worklog.worklogDate);
    setNotes(worklog.notes);
    setSelectedWorklogItems(worklog.worklogItems);
    setMode('view');
  };
  const updateAfterHours = (wi, newChecked) => {
    const updatedItems = cloneDeep(selectedWorklogItems);
    const idx = updatedItems.findIndex((el) => el.itemId === wi.itemId);
    updatedItems[idx] = {
      ...updatedItems[idx],
      afterHours: newChecked
    };
    setSelectedWorklogItems(updatedItems);
  };
  const modifyWorklog = () => {
    setWorklogLoading(true);
    const parsedObj = getParsedCrudData(
      worklog.worklogItems,
      selectedWorklogItems,
      'itemId'
    );
    const updateItems = parsedObj.updatedItems.map((el) => {
      return omit(el, ['status']);
    });
    const addItems = parsedObj.addedItems;
    const deleteItems = parsedObj.deletedItems.map((el) => {
      return pick(el, ['itemId']);
    });
    updateWorklog({
      variables: {
        worklogId: worklog.worklogId,
        worklog: {
          notes: notes,
          worklogDate: worklogDate,
          updateItems: updateItems,
          addItems: addItems,
          deleteItems: deleteItems
        }
      },
      input: {
        worklog: {
          notes: notes,
          worklogDate: worklogDate,
          updateItems: updateItems,
          addItems: addItems,
          deleteItems: deleteItems
        }
      }
    })
      .then((res) => {
        setWorklogLoading(false);
        setMode('view');
        if (res?.data?.updateWorklog) {
          EventEmitter.dispatch('worklogChange');
          toast('success', 'Worklog updated successfully');
        } else {
          // toast('error', 'Something went wrong');
        }
      })
      .finally(() => {
        setWorklogLoading(false);
      });
  };
  const handleBillableChange = (wi, newChecked) => {
    const updatedItems = cloneDeep(selectedWorklogItems);
    const idx = updatedItems.findIndex((el) => el.itemId === wi.itemId);
    updatedItems[idx] = {
      ...updatedItems[idx],
      billable: newChecked
    };
    setSelectedWorklogItems(updatedItems);
  };
  const deleteItem = (wi) => {
    const updatedItems = cloneDeep(selectedWorklogItems);
    const idx = updatedItems.findIndex((el) => el.itemId === wi.itemId);
    updatedItems.splice(idx, 1);
    setSelectedWorklogItems(updatedItems);
  };
  const handleQuantityChange = (wi, val) => {
    const updatedItems = cloneDeep(selectedWorklogItems);
    const idx = updatedItems.findIndex((el) => el.itemId === wi.itemId);
    updatedItems[idx] = {
      ...updatedItems[idx],
      qty: val
    };
    setSelectedWorklogItems(updatedItems);
  };

  const updateQuantity = (wi, val) => {
    const updatedItems = cloneDeep(selectedWorklogItems);
    const idx = updatedItems.findIndex((el) => el.itemId === wi.itemId);
    updatedItems[idx] = {
      ...updatedItems[idx],
      qty: val.toString()
    };
    setSelectedWorklogItems(updatedItems);
  };
  return mode === 'view' ? (
    <CardSection className="action-show">
      <AvatarHolder>
        <Avatar
          initials={`${worklog.technician.firstName[0]}${worklog.technician
            .lastName?.[0] || ''}`}
          size="extraSmall"
        />
        <B1 className="ml8">
          {worklog.technician?.name || <DisabledTag>NA</DisabledTag>}
        </B1>
        <div className="ml8 mr8">
          <FormattedMessage id="worklog.details.placeholder.on" />
        </div>
        <T2>
          <DateTimeFormatter type="date" date={worklog.worklogDate} />
        </T2>
        {isEditable && (
          <EditAction className="mlauto">
            <div className="action-visibility">
              <Button onClick={() => setMode('edit')}>
                <SVGIcon name="Edit" size="lg" />
              </Button>
            </div>
          </EditAction>
        )}
      </AvatarHolder>
      <Details>
        <div>
          {worklog.notes || (
            <T2>
              <FormattedMessage id="ticket.details.worklog.noNotes" />
            </T2>
          )}
        </div>
        {worklog.worklogItems.map((el) => {
          return <WorklogItemView el={el} intl={intl} />;
        })}
      </Details>
    </CardSection>
  ) : (
    <CardSection>
      <AvatarHolder>
        <Avatar
          initials={`${worklog.technician.firstName[0]}${worklog.technician
            .lastName?.[0] || ''}`}
          size="extraSmall"
        />
        <B1 className="ml8">
          {worklog.technician?.name || <DisabledTag>NA</DisabledTag>}
        </B1>
        <div className="ml8 mr8">
          <FormattedMessage id="worklog.details.placeholder.on" />
        </div>
        <DatePicker
          selected={worklogDate && dayjs(worklogDate).toDate()}
          onChange={(date) => {
            const formattedDate = dayjs(date).format('YYYY-MM-DD');
            setWorklogDate(formattedDate);
          }}
          placeholderText="YYYY-MM-DD"
          dateFormat="yyyy-MM-dd"
          title={getTranslatedText(intl, 'worklog.details.worklogDate')}
          required
          disabled={false}
        />
        <div className="mlauto">
          <ButtonGroup>
            <Button plain id="resetWorklog" onClick={resetWorklog}>
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              id="psaWorklogSave"
              primary
              loading={loading}
              onClick={modifyWorklog}
            >
              <FormattedMessage id="common.save" />
            </Button>
          </ButtonGroup>
        </div>
      </AvatarHolder>
      <div className="mt20">
        <TextField
          label=""
          placeholder={getTranslatedText(intl, 'ticket.details.worklog.pl')}
          value={notes}
          onChange={(val) => setNotes(val)}
          multiline={1}
        />
      </div>
      <div className="mt10">
        {selectedWorklogItems.map((el) => {
          // We cannot edit items which are approved/invoiced/ignored.
          return el.status === 'PENDING' ? (
            <WorklogItemEdit
              el={el}
              intl={intl}
              handleQuantityChange={handleQuantityChange}
              handleBillableChange={handleBillableChange}
              updateQuantity={updateQuantity}
              updateAfterHours={updateAfterHours}
              deleteItem={deleteItem}
            />
          ) : (
            <div>
              <WorklogItemView el={el} intl={intl} />
            </div>
          );
        })}
      </div>
    </CardSection>
  );
}

export default injectIntl(WorklogDetails);
